import api from './api';
import type { PaginatedResponse, Rental, Patient, Doctor } from '@/types';

// In proxy build (VITE_CLINIC_API_PREFIX=clinic) paths become /clinic/...
// In admin build (unset) paths remain /clinic-portal/... matching ClinicPortalController
const CLINIC_API = import.meta.env.VITE_CLINIC_API_PREFIX || 'clinic-portal';

export interface ClinicDashboardData {
  activeRentals: number;
  pendingReturns: number;
  recentOrders: ClinicOrderSummary[];
}

export interface ClinicOrderSummary {
  id: string;
  // The dashboard returns raw rental records, so the order number arrives in these
  // two fields. There has never been an `orderNumber` field on the wire — the type
  // used to declare one, so it read as undefined and every order fell back to a
  // fragment of its UUID. Use the orderNumber() helper.
  shopifyOrderNumber: string | null;
  legacyOrderId: number | null;
  patientName: string | null;
  status: string;
  createdAt: string;
}

export interface ClinicReportSummary {
  clinicId: string;
  startDate: string;
  endDate: string;
  orderCount: number;
  revenue: number;
  avgOrderValue: number;
}

export interface ListClinicOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export interface ListClinicPatientsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface ClinicProfile {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string | null;
  billingAddress1: string | null;
  billingAddress2: string | null;
  billingCity: string | null;
  billingProvince: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  isConsignmentClinic?: boolean;
}

export interface CreateOrderResponse {
  status: 'completed' | 'pending_payment';
  draftOrderId: string;
  shopifyOrderId?: string;
  shopifyOrderName?: string;
  invoiceUrl?: string;
}

export interface CreateReturnRequestDto {
  rentalId: string;
  notes?: string;
}

export interface ReturnRequestResponse {
  id: string;
  rentalId: string;
  status: string;
  createdAt: string;
}

export interface PendingExtensionRequest {
  id: string;
  rentalId: string;
  rental: {
    id: string;
    shopifyOrderNumber: string | null;
    patientName: string | null;
    rentalEndDate: string;
    rentalStatus: string;
  };
  weeksRequested: number | null;
  requestedBy: string | null;
  requestedAt: string;
  notes: string | null;
}

export interface AddOnProduct {
  shopifyProductId: string;
  title: string;
  handle: string;
  imageUrl: string | null;
  vendor?: string | null;
  options?: { name: string; values: string[] }[];
  variants: {
    id: string;
    title: string;
    sku: string;
    price: string;
    wholesalePriceCents: number | null;
    selectedOptions?: { name: string; value: string }[];
  }[];
  wholesalePriceCents: number | null;
  retailPriceCents: number | null;
}

export const clinicPortalApi = {
  getDashboard: async (): Promise<ClinicDashboardData> => {
    const response = await api.get<ClinicDashboardData>(`/${CLINIC_API}/dashboard`);
    return response.data;
  },

  getOrders: async (params: ListClinicOrdersParams = {}): Promise<PaginatedResponse<Rental>> => {
    const response = await api.get<PaginatedResponse<Rental>>(`/${CLINIC_API}/orders`, { params });
    return response.data;
  },

  getOrderDetail: async (id: string): Promise<Rental> => {
    const response = await api.get<Rental>(`/${CLINIC_API}/orders/${id}`);
    return response.data;
  },

  getAvailableSerials: async (id: string): Promise<{
    items: {
      rentalItemId: string;
      productSku: string;
      productName: string;
      alreadyAssigned: string[];
      availableSerials: string[];
    }[];
  }> => {
    const response = await api.get(`/${CLINIC_API}/orders/${id}/available-serials`);
    return response.data;
  },

  getPatients: async (params: ListClinicPatientsParams = {}): Promise<PaginatedResponse<Patient>> => {
    const response = await api.get<PaginatedResponse<{ patient: Patient }>>(`/${CLINIC_API}/patients`, { params });
    // Backend returns PatientClinicAssignment objects; unwrap to flat Patient[]
    return {
      ...response.data,
      data: response.data.data.map((a) => a.patient),
    };
  },

  getDoctors: async (): Promise<Doctor[]> => {
    const response = await api.get<{ doctor: Doctor }[]>(`/${CLINIC_API}/doctors`);
    // Backend returns ClinicDoctor association objects; unwrap to flat Doctor[]
    return response.data.map((a) => a.doctor);
  },

  getReportSummary: async (params: { startDate?: string; endDate?: string } = {}): Promise<ClinicReportSummary> => {
    const response = await api.get<ClinicReportSummary>(`/${CLINIC_API}/reports/summary`, { params });
    return response.data;
  },

  createReturnRequest: async (dto: CreateReturnRequestDto): Promise<ReturnRequestResponse> => {
    const response = await api.post<ReturnRequestResponse>(`/${CLINIC_API}/return-request`, dto);
    return response.data;
  },

  addOrderHistory: async (
    rentalId: string,
    data: { status: string; serialNumber?: string; notifyCustomer?: boolean; comment?: string },
  ): Promise<void> => {
    await api.post(`/${CLINIC_API}/orders/${rentalId}/history`, data);
  },

  getNotReturnedOrders: async (params: ListClinicOrdersParams = {}): Promise<PaginatedResponse<Rental>> => {
    const response = await api.get<PaginatedResponse<Rental>>(`/${CLINIC_API}/reports/not-returned`, { params });
    return response.data;
  },

  getOverdueOrders: async (params: ListClinicOrdersParams = {}): Promise<PaginatedResponse<Rental>> => {
    const response = await api.get<PaginatedResponse<Rental>>(`/${CLINIC_API}/reports/overdue`, { params });
    return response.data;
  },

  getTomorrowOverdueOrders: async (): Promise<Rental[]> => {
    const response = await api.get<Rental[]>(`/${CLINIC_API}/reports/tomorrow-overdue`);
    return response.data;
  },

  getFulfilledByClinicOrders: async (params: ListClinicOrdersParams = {}): Promise<PaginatedResponse<Rental>> => {
    const response = await api.get<PaginatedResponse<Rental>>(`/${CLINIC_API}/orders/fulfilled-by-clinic`, { params });
    return response.data;
  },

  getProfile: async (): Promise<ClinicProfile> => {
    const response = await api.get<ClinicProfile>(`/${CLINIC_API}/profile`);
    return response.data;
  },

  updateProfile: async (data: Partial<ClinicProfile>): Promise<ClinicProfile> => {
    const response = await api.put<ClinicProfile>(`/${CLINIC_API}/profile`, data);
    return response.data;
  },

  createPatient: async (data: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    age?: number;
    gender?: string;
    dateOfBirth?: string;
    streetAddress?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    referredByDoctorId?: string;
    status?: string;
  }): Promise<Patient> => {
    const response = await api.post<Patient>(`/${CLINIC_API}/patients`, data);
    return response.data;
  },

  getProducts: async (): Promise<any[]> => {
    const response = await api.get<any[]>(`/${CLINIC_API}/products`);
    return response.data;
  },

  getWraps: async (): Promise<any[]> => {
    const response = await api.get<any[]>(`/${CLINIC_API}/wraps`);
    return response.data;
  },

  getAddons: async (): Promise<AddOnProduct[]> => {
    const response = await api.get<AddOnProduct[]>(`/${CLINIC_API}/addons`);
    return response.data;
  },

  // generateReturnLabel intentionally removed — return labels are admin-only.

  getPendingRequests: async (): Promise<PendingExtensionRequest[]> => {
    const response = await api.get<PendingExtensionRequest[]>(`/${CLINIC_API}/pending-requests`);
    return response.data;
  },

  approveExtensionRequest: async (
    rentalId: string,
    historyId: string,
  ): Promise<{ success: boolean; newEndDate: string; weeksApproved: number }> => {
    const response = await api.post(
      `/${CLINIC_API}/orders/${rentalId}/approve-extension/${historyId}`,
    );
    return response.data;
  },

  denyExtensionRequest: async (
    rentalId: string,
    historyId: string,
    reason?: string,
  ): Promise<{ success: boolean }> => {
    const response = await api.post(
      `/${CLINIC_API}/orders/${rentalId}/deny-extension/${historyId}`,
      { reason },
    );
    return response.data;
  },

  createOrder: async (data: {
    patientId: string;
    productId: string;
    productName: string;
    productSku: string;
    variantId?: string;
    size?: string;
    weeklyRate: number;
    surgeryDate: string;
    rentalWeeks: number;
    isBilateral?: boolean;
    bilateralPrice?: number;
    isClinicFulfil: boolean;
    shippingAddressType?: string;
    specialInstructions?: string;
    addOns?: { name: string; sku: string; price: number; quantity: number }[];
    wrap?: {
      productId: string;
      productTitle: string;
      variantId?: string;
      variantTitle?: string;
      sku?: string;
    };
  }): Promise<CreateOrderResponse> => {
    const response = await api.post<CreateOrderResponse>(`/${CLINIC_API}/orders`, data);
    return response.data;
  },
};

export default clinicPortalApi;
